import { createApi } from '@reduxjs/toolkit/query/react';
import { api } from './api';

/**
 * RTK Query on top of the shared axios instance, so the auth header and the
 * 401/403 -> logout interceptor keep applying.
 */
const axiosBaseQuery =
  () =>
  async ({ url, method = 'get', params, data }, { signal }) => {
    try {
      const response = await api({ url, method, params, data, signal });
      return { data: response.data };
    } catch (error) {
      if (signal?.aborted || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
        return { error: { status: 'ABORTED', message: 'aborted' } };
      }
      return {
        error: {
          status: error.response?.status,
          message: error.response?.data?.message || error.message || 'Request failed',
        },
      };
    }
  };

export const statApi = createApi({
  reducerPath: 'statApi',
  baseQuery: axiosBaseQuery(),
  // Stats are append-only time series; a short window avoids hammering the API
  // while the user flips between filters.
  keepUnusedDataFor: 120,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    getStat: builder.query({
      query: ({ interval, project, campaignId, start, end, timezone }) => ({
        url: '/stat',
        params: {
          time_interval: interval,
          project: project || undefined,
          campaignid: campaignId || undefined,
          start,
          end,
          time_zone: timezone,
        },
      }),
      transformResponse: (response) => (Array.isArray(response) ? response : []),
    }),
    getProjects: builder.query({
      query: () => ({ url: '/projects' }),
      transformResponse: (response) => (Array.isArray(response) ? response : []),
    }),
  }),
});

export const { useGetStatQuery, useGetProjectsQuery } = statApi;

/** Human-readable message for an RTK Query error object. */
export const describeQueryError = (error) =>
  error?.message || (error?.status ? `Request failed (${error.status})` : 'Request failed');
